import { redirect } from '@tanstack/react-router';
import type { ProfileResponse } from '@bliks-io/contracts';
import type { V2RouterContext } from '../../routes/__root';
import { profileQueryOptions } from '@/api/queries/useProfile';
import type { LockRoute } from '@/constants/routes';

/**
 * The lock screen an org belongs on before it may be used, or null when
 * nothing locks it. A seat overrun outranks setup (ADR 0011): the repair
 * actions pass both gates, so the overrun can be cleared mid-Ersteinrichtung,
 * whereas the wizard's own writes are refused while over cap.
 *
 * The setup branch is keyed on the same `InitialSetup.read` permission the
 * `$slug` parent guard uses to admit the wizard, so every place that routes the
 * gate agrees on who belongs in the wizard; a divergence here would loop
 * $slug ⇄ setup screen.
 */
export function resolveLockRedirect(profile: ProfileResponse): LockRoute | null {
  if (profile.seatOverrun) return '/$slug/seat-overrun';
  if (profile.setupComplete) return null;

  return profile.permissions?.InitialSetup?.read ? '/$slug/initial-setup' : '/$slug/setup-pending';
}

/**
 * The `beforeLoad` every lock route shares: stay put when the profile resolves
 * to this very lock, otherwise leave for the lock that outranks it, or for the
 * org home once the org is usable again.
 */
export function lockRouteBeforeLoad(self: LockRoute) {
  return async ({
    context,
    params
  }: {
    context: Pick<V2RouterContext, 'queryClient'>;
    params: { slug: string };
  }) => {
    const profile = await context.queryClient.ensureQueryData(profileQueryOptions);
    const lockRedirect = resolveLockRedirect(profile);
    if (lockRedirect === self) return;
    throw redirect({ to: lockRedirect ?? '/$slug', params: { slug: params.slug } });
  };
}
